import React, { useMemo, useState } from 'react';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import HostStudentRosterCard from '../../../components/host/HostStudentRosterCard';
import type { HostStudent } from '../../../models/HostStudent';

export interface HostStudentsRosterProps {
  students: HostStudent[];
  loading: boolean;
  fetchError: string | null;
  onDismissError: () => void;
  onRetry: () => void;
  onAdd: () => void;
  onEdit: (student: HostStudent) => void;
  onReport: (student: HostStudent) => void;
  onUnblock: (student: HostStudent) => void;
  onRemove: (student: HostStudent) => void;
  canReport: (student: HostStudent) => boolean;
  canUnblock: (student: HostStudent) => boolean;
}

const rosterPanelSx = {
  borderRadius: 2.5,
  border: 1,
  borderColor: 'divider',
  overflow: 'hidden',
  backgroundImage: (theme: Theme) =>
    `linear-gradient(168deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(
      theme.palette.primary.dark,
      0.16
    )} 100%)`,
};

function studentSearchHaystack(student: HostStudent): string {
  const first = student.studentProfile.firstName ?? '';
  const last = student.studentProfile.lastName ?? '';
  return [student.name, first, last, student.phoneNumber].filter(Boolean).join(' ').toLowerCase();
}

export default function HostStudentsRoster({
  students,
  loading,
  fetchError,
  onDismissError,
  onRetry,
  onAdd,
  onEdit,
  onReport,
  onUnblock,
  onRemove,
  canReport,
  canUnblock,
}: HostStudentsRosterProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return students;
    return students.filter((student) => studentSearchHaystack(student).includes(q));
  }, [students, search]);

  const showToolbar = !loading && students.length > 0;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.75 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: 'grid',
                placeItems: 'center',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                border: 1,
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.28),
              }}
            >
              <SchoolOutlinedIcon sx={{ color: 'primary.light', fontSize: 22 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
              {t('hostStudents.title', 'Students')}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, lineHeight: 1.6 }}>
            {t(
              'hostStudents.subtitle',
              'Your school roster — add students by phone, keep school profiles, and pick them for lessons.'
            )}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<PersonAddAlt1OutlinedIcon />}
          onClick={onAdd}
          sx={{
            textTransform: 'none',
            letterSpacing: 0,
            fontWeight: 700,
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {t('hostStudents.add.title', 'Add student')}
        </Button>
      </Box>

      {fetchError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={onDismissError}
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              {t('hostUsers.retry', 'Retry')}
            </Button>
          }
        >
          {fetchError}
        </Alert>
      )}

      <Box sx={rosterPanelSx}>
        {showToolbar && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            sx={{
              px: { xs: 2, sm: 2.5 },
              py: 1.75,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: (theme) => alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.12 : 0.04),
            }}
          >
            <TextField
              size="small"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('hostStudents.searchPlaceholder', 'Search by name or phone')}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: { sm: 280 },
                width: { xs: '100%', sm: 280 },
                '& .MuiOutlinedInput-root': {
                  bgcolor: (theme) => alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.04 : 0.7),
                  borderRadius: 1.5,
                },
              }}
            />
            {!loading && (
              <Chip
                label={t('hostStudents.countLabel', '{{count}} students', { count: students.length })}
                size="small"
                sx={{ fontWeight: 700, borderRadius: 1.5, alignSelf: { xs: 'flex-start', sm: 'center' } }}
              />
            )}
          </Stack>
        )}

        <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
          {loading ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: '1fr 1fr 1fr' },
                gap: 2,
              }}
            >
              {[0, 1, 2, 3].map((key) => (
                <Skeleton key={key} variant="rounded" height={168} sx={{ borderRadius: 2 }} />
              ))}
            </Box>
          ) : fetchError ? null : students.length === 0 ? (
            <Stack alignItems="center" spacing={2} sx={{ py: { xs: 4, sm: 5 }, px: 2, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                  border: 1,
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.24),
                }}
              >
                <SchoolOutlinedIcon sx={{ fontSize: 34, color: 'primary.light' }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>
                  {t('hostStudents.emptyTitle', 'Build your student roster')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
                  {t(
                    'hostStudents.emptyHint',
                    'Add someone by phone, or they will show up here after they book a lesson.'
                  )}
                </Typography>
              </Box>
            </Stack>
          ) : filtered.length === 0 ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
              <Typography variant="body1" fontWeight={600}>
                {t('hostStudents.noMatches', 'No students match your search.')}
              </Typography>
              <Button size="small" onClick={() => setSearch('')}>
                {t('hostStudents.clearSearch', 'Clear search')}
              </Button>
            </Stack>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: '1fr 1fr 1fr' },
                gap: 2,
              }}
            >
              {filtered.map((student) => (
                <HostStudentRosterCard
                  key={student.id}
                  student={student}
                  onEdit={onEdit}
                  onReport={onReport}
                  onUnblock={onUnblock}
                  onRemove={onRemove}
                  canReport={canReport(student)}
                  canUnblock={canUnblock(student)}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
